import { i18n } from "../plugin/vue-i18n";

export interface SlideLike {
  slideName?: string;
}

/**
 * Replaces legacy default names like "Page N" / "Slide N" with localized labels.
 * Mutates the provided slides array in-place.
 */
export const localizeLegacyNames = (
  slides: SlideLike[] = [],
  t: (key: string, params?: Record<string, unknown>) => string,
  match: RegExp,
  labelKey: string
): void => {
  try {
    slides.forEach((slide, index) => {
      const name = (slide?.slideName || "").toString();
      if (match.test(name)) {
        slide.slideName = t(labelKey, { n: index + 1 });
      }
    });
  } catch (error) {
    console.error("localizeLegacyNames: failed to localize legacy names", error);
  }
};

/**
 * Maps a legacy captcha field label to the new "Bot Protection" label.
 *
 * Forms saved before the Captcha → Bot Protection rename carry whatever
 * `fields.customized.captcha` resolved to at creation time, in the creator's
 * locale. The legacy-label allow-list is derived from each loaded locale's
 * value of that key, so the shim stays in sync with the locale files
 * automatically and works for cross-locale viewing.
 *
 * User-customized labels pass through untouched.
 *
 * Intended to run ONCE at form-load time via {@link migrateLegacyCaptchaLabels}
 * — not at render time.
 */
const mapLegacyCaptchaLabel = (stored: string): string => {
  if (typeof stored !== "string") return "";
  const trimmed = stored.trim();
  if (!trimmed) return stored;
  // vue-i18n compiles message values into functions, so we cannot compare
  // the raw value tree directly — use t({ locale }) to resolve each locale's
  // captcha string and compare against the stored label.
  const allMessages = i18n.global.messages.value as Record<string, unknown>;
  type SupportedLocale = typeof i18n.global.locale.value;
  const localeKeys = Object.keys(allMessages) as SupportedLocale[];
  for (const locale of localeKeys) {
    const captchaValue = i18n.global.t(
      "fields.customized.captcha",
      {},
      { locale }
    );
    if (captchaValue === trimmed) {
      return i18n.global.t("fields.customized.botProtection");
    }
  }
  return stored;
};

/**
 * Walks an array of form fields and rewrites legacy captcha labels in-place.
 * Call once after loading form data from the API.
 */
export const migrateLegacyCaptchaLabels = (
  fields: Array<{ tag?: string; label?: string }> | undefined | null
): void => {
  if (!Array.isArray(fields)) return;
  for (const field of fields) {
    if (field?.tag === "captcha" && typeof field.label === "string") {
      field.label = mapLegacyCaptchaLabel(field.label);
    }
  }
};

/**
 * Walks an array of survey/quiz slides and migrates legacy captcha labels
 * inside each slide's slideData. Call once after loading the form data.
 */
export const migrateLegacyCaptchaLabelsInSlides = (
  slides:
    | Array<{ slideData?: Array<{ tag?: string; label?: string }> }>
    | undefined
    | null
): void => {
  if (!Array.isArray(slides)) return;
  for (const slide of slides) {
    migrateLegacyCaptchaLabels(slide?.slideData);
  }
};
